<template>
    <div class="modal fade" ref="surveyModal" data-keyboard="false" data-backdrop="static" id="survey-modal" tabindex="-1">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content">
                <div class="modal-body">
                    <div class="pt-4 px-3 px-xl-6">
                        <a type="button" class="modal-close-btn" data-dismiss="modal">
                            <img src="/images/global/close-icon.svg">
                        </a>
                        <div class="modal-heading mb-3">Got a minute? We'd love your feedback.</div>

                        <div v-if="!success">
                            <form @submit.prevent="submit">
                                <template v-for="(question, index) in survey.questions">
                                    <hr v-if="index > 0" :key="'hr_' + question.id"/>

                                    <div class="form-group" :key="question.id">
                                        <template v-if="question.type === 'multiple_choice'">
                                            <p class="mb-2">
                                                {{ question.question }}
                                                <small v-if="question.multiple" class="text-muted d-block">Select all that apply.</small>
                                            </p>
                                            <template v-if="question.multiple">
                                                <div
                                                    v-for="option in question.options"
                                                    :key="option.id"
                                                    class="form-check pt-0 pl-1"
                                                >
                                                    <FormCheckbox
                                                        :class="{'is-invalid': validationErrors['question_' + question.id]}"
                                                        :id="'question_' + question.id + '_' + option.id"
                                                        :value="option.option_text"
                                                        v-model="responses[question.id]"
                                                    />
                                                    <FormLabel class="form-check-label" :for="'question_' + question.id + '_' + option.id">
                                                        {{ option.option_text }}
                                                    </FormLabel>
                                                </div>

                                                <template v-if="question.allows_other">
                                                    <div class="form-check pt-0 pl-1 mb-2">
                                                        <FormCheckbox
                                                            :class="{'is-invalid': validationErrors['question_' + question.id]}"
                                                            :id="'question_' + question.id + '_other'"
                                                            value="Other"
                                                            v-model="responses[question.id]"
                                                        />
                                                        <FormLabel class="form-check-label" :for="'question_' + question.id + '_other'">
                                                            Other (please specify)
                                                        </FormLabel>
                                                    </div>
                                                    <FormTextarea
                                                        v-model="otherText[question.id]"
                                                        :disabled="!responses[question.id].includes('Other')"
                                                        :class="{'is-invalid': validationErrors['question_' + question.id + '_other']}"
                                                        placeholder="Please specify..."
                                                    />
                                                </template>
                                            </template>
                                            <template v-else>
                                                <div
                                                    v-for="option in question.options"
                                                    :key="option.id"
                                                    class="form-check pt-0"
                                                >
                                                    <FormRadio
                                                        :class="{'is-invalid': validationErrors['question_' + question.id]}"
                                                        :id="'question_' + question.id + '_' + option.id"
                                                        :value="option.option_text"
                                                        v-model="responses[question.id]"
                                                    />
                                                    <FormLabel class="form-check-label" :for="'question_' + question.id + '_' + option.id">
                                                        {{ option.option_text }}
                                                    </FormLabel>
                                                </div>

                                                <template v-if="question.allows_other">
                                                    <div class="form-check pt-0 mb-2">
                                                        <FormRadio
                                                            :class="{'is-invalid': validationErrors['question_' + question.id]}"
                                                            :id="'question_' + question.id + '_other'"
                                                            value="Other"
                                                            v-model="responses[question.id]"
                                                        />
                                                        <FormLabel class="form-check-label" :for="'question_' + question.id + '_other'">
                                                            Other (please specify)
                                                        </FormLabel>
                                                    </div>
                                                    <FormTextarea
                                                        v-model="otherText[question.id]"
                                                        :disabled="responses[question.id] !== 'Other'"
                                                        :class="{'is-invalid': validationErrors['question_' + question.id + '_other']}"
                                                        placeholder="Please specify..."
                                                    />
                                                </template>
                                            </template>
                                        </template>
                                        <template v-else>
                                            <FormLabel :for="'question_' + question.id">{{ question.question }}</FormLabel>
                                            <FormTextarea
                                                :id="'question_' + question.id"
                                                :class="{'is-invalid': validationErrors['question_' + question.id]}"
                                                v-model="responses[question.id]"
                                            />
                                        </template>

                                        <ValidationError v-if="validationErrors['question_' + question.id]">
                                            {{ validationErrors['question_' + question.id] }}
                                        </ValidationError>
                                    </div>
                                </template>

                                <ValidationError v-if="validationErrors.survey_id">{{ validationErrors.survey_id }}</ValidationError>
                                <FormButton class="mt-4" :pending="pending" pending-text="Submitting">Submit</FormButton>
                            </form>
                            <p v-if="errorMessage && errorStatusCode !== 422" class="small mt-3 text-danger">{{ errorMessage }}</p>
                        </div>

                        <div v-else class="mt-3 alert alert-success" role="alert">
                            <p class="mb-0">Thank you for your feedback!</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import FormButton from '@/components/form/FormButton';
import FormCheckbox from '@/components/form/FormCheckbox';
import FormTextarea from '@/components/form/FormTextarea';
import FormLabel from '@/components/form/FormLabel';
import FormRadio from '@/components/form/FormRadio';
import ValidationError from '@/components/form/ValidationError';
import FormMixin from '@/mixins/FormMixin';
import SurveyResponseService from '@/services/survey-response.service';

export default {
    components: { FormButton, FormCheckbox, FormTextarea, FormLabel, FormRadio, ValidationError },

    mixins: [FormMixin],

    props: {
        survey: {
            type: Object,
            required: true,
        },
        autoShow: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            responses: this.buildInitialResponses(),
            otherText: this.buildInitialOtherText(),
        };
    },

    mounted() {
        const $modal = $(this.$refs.surveyModal);

        $modal.on('hide.bs.modal', () => {
            this.resetForm();
        });

        if (this.autoShow) {
            $modal.modal('show');
        }
    },

    methods: {
        buildInitialResponses() {
            const responses = {};
            this.survey.questions.forEach(q => {
                responses[q.id] = q.multiple ? [] : '';
            });
            return responses;
        },

        buildInitialOtherText() {
            const otherText = {};
            this.survey.questions.forEach(q => {
                if (q.allows_other) {
                    otherText[q.id] = '';
                }
            });
            return otherText;
        },

        buildPayload() {
            const payload = { survey_id: this.survey.id };

            this.survey.questions.forEach(question => {
                const value = this.responses[question.id];
                payload[`question_${question.id}`] = Array.isArray(value)
                    ? value.join(', ')
                    : (value || '');

                if (question.allows_other) {
                    payload[`question_${question.id}_other`] = this.otherText[question.id] || '';
                }
            });

            return payload;
        },

        async submit() {
            this.pending = true;
            this.errorMessage = '';
            this.validationErrors = {};
            this.errorStatusCode = null;

            try {
                await SurveyResponseService.store(this.buildPayload());
                this.success = true;
            } catch (error) {
                this.handleError(error);
            } finally {
                this.pending = false;
            }
        },

        resetForm() {
            this.responses = this.buildInitialResponses();
            this.otherText = this.buildInitialOtherText();
            this.success = false;
            this.validationErrors = {};
            this.errorMessage = '';
        },
    },
};
</script>
